import { useId, useRef } from "react";
import { Btn, Field, Input, Toggle, useModalFocus } from "../../components/Shared";

// Small pieces the Management cards share. Shared.jsx is a byte-identical boku-ui copy and has
// no switch row or plain confirmation, so they live here.

// Visually hidden, still read out: `Btn` forwards no aria-label, so the words that tell two
// "Copy" buttons apart sit inside the button itself.
export const SR_ONLY = { position: "absolute", width: 1, height: 1, padding: 0, margin: -1,
                         overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap", border: 0 };

export const LEGEND = { fontSize: 12, fontWeight: 600, color: "#374151", letterSpacing: "0.05em",
                        textTransform: "uppercase", marginBottom: 6, padding: 0 };

export function SuccessNote({ children }) {
  if (!children) return null;
  return (
    <div role="status" style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(0,150,64,0.06)", border: "1px solid rgba(0,150,64,0.25)", color: "#065f46", fontSize: 13, margin: "0 0 14px" }}>
      {children}
    </div>
  );
}

/** A labelled on/off switch with the sentence that says what "on" means. */
export function SwitchRow({ label, checked, onChange, hint, disabled }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
      <Toggle checked={checked} onChange={onChange} label={label} disabled={disabled} />
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a" }}>
          {label}: <span style={{ fontWeight: 500 }}>{checked ? "on" : "off"}</span>
        </div>
        {hint && <div style={{ fontSize: 12, color: "#4b5563", marginTop: 2 }}>{hint}</div>}
      </div>
    </div>
  );
}

/** An address the portal knows and Keycloak's admin must enter: read-only, with Copy. */
export function CopyField({ label, name, hint, value, onCopy, copied }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <Field label={label} hint={hint}>
        <Input value={value || ""} readOnly />
      </Field>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
        <Btn variant="secondary" onClick={onCopy}>Copy<span style={SR_ONLY}> {name}</span></Btn>
        {copied && <span role="status" style={{ fontSize: 12, color: "#374151" }}>{copied}</span>}
      </div>
    </div>
  );
}

/**
 * A yes/no question before something that can lock people out. Focus moves into the dialog and
 * stays inside on Tab, Escape and Cancel close it, and focus goes back to the opener
 * (useModalFocus from Shared.jsx, as ConfirmDialog; the caller's rememberFocus also still runs).
 */
export function AskDialog({ title, confirmLabel, busy, onConfirm, onClose, children }) {
  const ref = useRef(null);
  const id = useId();
  useModalFocus(ref, onClose);
  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }}
         style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 220, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div ref={ref} role="dialog" aria-modal="true" aria-labelledby={`${id}-t`} tabIndex={-1}
           style={{ background: "#fff", borderRadius: 16, border: "1px solid #e5e7eb", maxWidth: 560, width: "100%", maxHeight: "90vh", overflowY: "auto", padding: "24px 28px", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
        <h2 id={`${id}-t`} style={{ margin: "0 0 12px", fontSize: 18, fontWeight: 700, color: "#92400e" }}>{title}</h2>
        {children}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
          <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn variant="danger" disabled={busy} onClick={onConfirm}>{busy ? "Saving…" : confirmLabel}</Btn>
        </div>
      </div>
    </div>
  );
}

export function formatWhen(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}
